import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowRight, Mail, MessageCircle, Phone, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("rb100_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/messages");
      setItems(data || []);
    } catch (e) {
      console.error(e);
      toast.error("تعذّر تحميل الرسائل");
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/messages/${id}/read`);
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (e) {
      console.error(e);
      toast.error("فشل التحديث");
    }
  };

  const filtered = items.filter((m) => (filter === "unread" ? !m.read : true));
  const unreadCount = items.filter((m) => !m.read).length;

  return (
    <div data-testid="admin-messages" className="min-h-screen bg-white text-rb-ink">
      <div className="bg-white border-b border-rb-border sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              data-testid="messages-back-btn"
              className="flex items-center gap-2 text-sm text-rb-textMuted hover:text-rb-ink"
            >
              <ArrowRight className="w-4 h-4" />
              لوحة الإدارة
            </Link>
            <div className="h-5 w-px bg-rb-border" />
            <h1 className="font-heading text-lg font-extrabold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-rb-red" />
              صندوق الرسائل
              {unreadCount > 0 && (
                <span className="bg-rb-red text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
          </div>
          <div className="flex border border-rb-border bg-rb-surface">
            {[
              { key: "all", label: `الكل (${items.length})` },
              { key: "unread", label: `غير مقروءة (${unreadCount})` },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                data-testid={`messages-filter-${t.key}`}
                className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                  filter === t.key ? "bg-rb-ink text-white" : "text-rb-textMuted hover:text-rb-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="w-7 h-7 animate-spin text-rb-red" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-rb-textMuted">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>لا توجد رسائل {filter === "unread" ? "غير مقروءة" : ""}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((m) => (
              <article
                key={m.id}
                data-testid={`message-card-${m.id}`}
                className={`bg-white border p-5 transition-colors ${
                  m.read ? "border-rb-border" : "border-rb-red border-l-4"
                }`}
              >
                <header className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className={m.read ? "text-rb-textMuted" : "text-rb-red"}>
                      {m.read ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5 fill-current" />}
                    </div>
                    <div>
                      <div className="font-heading font-bold text-base text-rb-ink">{m.name}</div>
                      <div className="flex flex-wrap gap-3 text-xs text-rb-textMuted mt-1">
                        {m.email && (
                          <span className="flex items-center gap-1" dir="ltr">
                            <Mail className="w-3 h-3" />
                            {m.email}
                          </span>
                        )}
                        {m.mobile && (
                          <span className="flex items-center gap-1" dir="ltr">
                            <Phone className="w-3 h-3" />
                            {m.mobile}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-rb-textMuted text-left">
                    {new Date(m.created_at).toLocaleString("ar-SA", {
                      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                </header>
                {m.subject && (
                  <div className="text-sm font-bold text-rb-ink mb-2">{m.subject}</div>
                )}
                <p className="text-sm text-rb-ink whitespace-pre-wrap leading-relaxed border-t border-rb-border pt-3">
                  {m.message}
                </p>
                <footer className="mt-4 pt-3 border-t border-rb-border flex flex-wrap gap-2 justify-end">
                  {m.mobile && (
                    <a
                      href={`https://wa.me/${m.mobile.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`message-whatsapp-${m.id}`}
                      className="text-xs font-bold bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 transition-colors"
                    >
                      رد عبر واتساب
                    </a>
                  )}
                  {m.email && (
                    <a
                      href={`mailto:${m.email}`}
                      data-testid={`message-email-${m.id}`}
                      className="text-xs font-bold border border-rb-border hover:border-rb-ink text-rb-ink px-3 py-1.5 transition-colors"
                    >
                      رد بالبريد
                    </a>
                  )}
                  {!m.read && (
                    <button
                      onClick={() => markRead(m.id)}
                      data-testid={`message-mark-read-${m.id}`}
                      className="text-xs font-bold border border-rb-red text-rb-red hover:bg-rb-red hover:text-white px-3 py-1.5 transition-colors"
                    >
                      وضع علامة كمقروءة
                    </button>
                  )}
                </footer>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
